import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, User, Bot, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { Message } from '../lib/types';
import { copyToClipboard } from '../lib/utils';
import { useI18n } from '../i18n';
import { useChatStore } from '../stores/chatStore';

interface ChatMessageProps {
    message: Message;
    isStreaming?: boolean;
    onImageClick?: (imagePath: string) => void;
}

export default function ChatMessage({ message, isStreaming, onImageClick }: ChatMessageProps) {
    const isUser = message.role === 'user';
    const isAssistant = message.role === 'assistant';
    const { formatRelativeTime } = useI18n();
    const deleteMessages = useChatStore((s) => s.deleteMessages);

    const handleDelete = () => {
        if (confirm('이 메시지를 삭제하시겠습니까?')) {
            deleteMessages([message.id]);
        }
    };

    return (
        <div
            className={`group relative flex gap-4 p-4 ${isUser ? 'bg-dark-900/50' : 'bg-transparent'
                }`}
        >
            {/* Delete button - visible on hover */}
            <button
                onClick={handleDelete}
                className="absolute top-2 right-2 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-dark-700"
                title="삭제"
            >
                <Trash2 className="w-4 h-4 text-red-400" />
            </button>
            {/* Avatar */}
            <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isUser
                    ? 'bg-dark-700'
                    : 'bg-gradient-to-br from-accent-500 to-purple-600'
                    }`}
            >
                {isUser ? (
                    <User className="w-4 h-4 text-dark-300" />
                ) : (
                    <Bot className="w-4 h-4 text-white" />
                )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-sm font-medium text-dark-200">
                        {isUser ? 'You' : 'AI Assistant'}
                    </span>
                    {!isUser && message.model && (
                        <span className="text-xs text-accent-400 bg-accent-500/10 px-1.5 py-0.5 rounded">
                            {message.model.replace('models/', '')}
                        </span>
                    )}
                    <span className="text-xs text-dark-500">
                        {formatRelativeTime(message.created_at)}
                    </span>
                </div>

                {/* Message content */}
                <div className={`prose-dark ${isStreaming ? 'streaming-text' : ''}`}>
                    {isAssistant ? (
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                                code({ node, className, children, ...props }) {
                                    const match = /language-(\w+)/.exec(className || '');
                                    const codeString = String(children).replace(/\n$/, '');

                                    if (match) {
                                        return (
                                            <CodeBlock
                                                language={match[1]}
                                                code={codeString}
                                            />
                                        );
                                    }

                                    return (
                                        <code className={className} {...props}>
                                            {children}
                                        </code>
                                    );
                                },
                            }}
                        >
                            {message.content}
                        </ReactMarkdown>
                    ) : (
                        <p className="text-dark-200 whitespace-pre-wrap">{message.content}</p>
                    )}
                </div>

                {/* Media attachment */}
                {message.r2_path && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {message.type === 'image' && message.r2_path.split(',').map((path, index) => (
                            <img
                                key={index}
                                src={`${import.meta.env.VITE_API_URL || '/api'}/media/file/${path.trim()}`}
                                alt={`Attached image ${index + 1}`}
                                className="max-w-sm rounded-lg border border-dark-800 cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => onImageClick?.(path.trim())}
                            />
                        ))}
                        {message.type === 'video' && (
                            <video
                                src={`${import.meta.env.VITE_API_URL || '/api'}/media/file/${message.r2_path}`}
                                controls
                                className="max-w-sm rounded-lg border border-dark-800"
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// Code block with copy functionality
function CodeBlock({ language, code }: { language: string; code: string }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        const success = await copyToClipboard(code);
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div className="relative group my-3">
            <div className="flex items-center justify-between px-4 py-2 bg-dark-800 rounded-t-lg border border-b-0 border-dark-700">
                <span className="text-xs text-dark-400 font-mono">{language}</span>
                <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 text-xs text-dark-400 hover:text-dark-200 transition-colors"
                >
                    {copied ? (
                        <>
                            <Check className="w-3.5 h-3.5" />
                            Copied!
                        </>
                    ) : (
                        <>
                            <Copy className="w-3.5 h-3.5" />
                            Copy
                        </>
                    )}
                </button>
            </div>
            <SyntaxHighlighter
                language={language}
                style={oneDark}
                customStyle={{
                    margin: 0,
                    borderRadius: '0 0 0.5rem 0.5rem',
                    border: '1px solid rgb(51 65 85)',
                    borderTop: 'none',
                }}
            >
                {code}
            </SyntaxHighlighter>
        </div>
    );
}

// Streaming message component
export function StreamingMessage({ content }: { content: string }) {
    return (
        <div className="flex gap-4 p-4">
            {/* Avatar */}
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-white" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-dark-200">AI Assistant</span>
                    <div className="typing-indicator text-accent-400">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>

                <div className="prose-dark">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                    <span className="inline-block w-2 h-4 bg-accent-500 animate-pulse ml-1" />
                </div>
            </div>
        </div>
    );
}
